import { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatMoney, getCurrency } from "@/lib/prefs";
import { Package, AlertTriangle, Loader2, Save, Share2, Trash2, Plus, Boxes, Upload, Scale } from "lucide-react";
import { toast } from "sonner";
import { SkeletonCard } from "@/components/GlassSkeleton";
import LogPrepBatchDialog from "@/components/LogPrepBatchDialog";
import EmptyState from "@/components/EmptyState";
import InventoryShareDialog from "@/components/InventoryShareDialog";
import InventoryImportDialog from "@/components/InventoryImportDialog";
import { IngredientForm } from "@/pages/Ingredients";
import DataExportButton from "@/components/DataExportButton";
import { printInventorySnapshot } from "@/lib/inventoryPdf";
import { buildCsv, downloadCsv, stampedFilename } from "@/lib/csvExport";
import { buildWorksheet, downloadWorkbook } from "@/lib/xlsxExport";
import { Link } from "react-router-dom";
import { useModuleTour, TourButton } from "@/components/useModuleTour";

export default function Inventory() {
  const [ingredients, setIngredients] = useState([]);
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState({}); // ingredient_id -> { quantity, par_level }
  const [saving, setSaving] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [newIngredientOpen, setNewIngredientOpen] = useState(false);
  const [batchDialogOpen, setBatchDialogOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    const [i, inv] = await Promise.all([api.get("/ingredients"), api.get("/inventory")]);
    setIngredients(i.data);
    setInventory(inv.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const invByIng = useMemo(() => Object.fromEntries(inventory.map((x) => [x.ingredient_id, x])), [inventory]);

  const getRow = (ing) => {
    const draft = edits[ing.id];
    if (draft) return draft;
    const existing = invByIng[ing.id];
    return { quantity: existing?.quantity ?? 0, par_level: existing?.par_level ?? 0 };
  };

  const setRow = (ingId, patch) => setEdits((e) => ({ ...e, [ingId]: { ...getRow({ id: ingId }), ...patch } }));

  const saveAll = async () => {
    setSaving(true);
    try {
      const updates = Object.entries(edits);
      for (const [ingredient_id, row] of updates) {
        await api.post("/inventory", {
          ingredient_id,
          quantity: Number(row.quantity) || 0,
          par_level: Number(row.par_level) || 0,
        });
      }
      toast.success(`Saved ${updates.length} update${updates.length === 1 ? "" : "s"}`);
      setEdits({});
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const removeRow = async (ing) => {
    const invRow = invByIng[ing.id];
    if (!invRow) {
      // Nothing to remove: no stored inventory row for this ingredient.
      // Just clear the local draft so the input goes back to defaults.
      setEdits((e) => { const c = { ...e }; delete c[ing.id]; return c; });
      return;
    }
    if (!window.confirm(`Remove "${ing.name}" from tracked stock? The ingredient itself will stay in the pantry.`)) return;
    try {
      await api.delete(`/inventory/${invRow.id}`);
      toast.success("Removed from inventory");
      setEdits((e) => { const c = { ...e }; delete c[ing.id]; return c; });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  };

  const lowStock = ingredients
    .map((ing) => {
      const inv = invByIng[ing.id];
      if (!inv || inv.par_level <= 0) return null;
      if (inv.quantity < inv.par_level) return { ing, inv };
      return null;
    })
    .filter(Boolean);

  // Build the export row set from the CURRENT screen state (draft edits
  // win over saved rows so what you see is what you download).
  const exportRows = () => ingredients.map((ing) => {
    const row = getRow(ing);
    const qty = Number(row.quantity) || 0;
    const par = Number(row.par_level) || 0;
    const unit_price = Number(ing.purchase_price) || 0;
    const purchase_qty = Number(ing.purchase_qty) || 1;
    const value = qty * (unit_price / purchase_qty);
    return {
      name: ing.name,
      category: ing.category || "",
      quantity: qty,
      par_level: par,
      unit: ing.purchase_unit || "",
      unit_price,
      stock_value: value,
      below_par: par > 0 && qty < par,
    };
  });

  const handleCsvExport = () => {
    const rows = exportRows();
    const columns = [
      { label: "Ingredient", type: "string", get: (r) => r.name },
      { label: "Category", type: "string", get: (r) => r.category },
      { label: "On hand", type: "number", get: (r) => r.quantity },
      { label: "Par level", type: "number", get: (r) => r.par_level },
      { label: "Unit", type: "string", get: (r) => r.unit },
      { label: "Unit price", type: "money", get: (r) => r.unit_price },
      { label: "Stock value", type: "money", get: (r) => r.stock_value },
      { label: "Below par", type: "string", get: (r) => (r.below_par ? "Yes" : "") },
    ];
    downloadCsv(stampedFilename("inventory", "csv"), buildCsv(rows, columns));
    toast.success(`Inventory CSV: ${rows.length} rows exported`);
  };

  const inventoryColumns = () => [
    { label: "Ingredient", type: "string", get: (r) => r.name },
    { label: "Category", type: "string", get: (r) => r.category },
    { label: "On hand", type: "number", get: (r) => r.quantity },
    { label: "Par level", type: "number", get: (r) => r.par_level },
    { label: "Unit", type: "string", get: (r) => r.unit },
    { label: "Unit price", type: "money", get: (r) => r.unit_price },
    { label: "Stock value", type: "money", get: (r) => r.stock_value },
    { label: "Below par", type: "string", get: (r) => (r.below_par ? "Yes" : "") },
  ];

  const handleXlsxExport = () => {
    const rows = exportRows();
    const sheet = buildWorksheet(rows, inventoryColumns(), "Inventory", getCurrency());
    downloadWorkbook([sheet], "inventory");
    toast.success(`Inventory Excel: ${rows.length} rows exported`);
  };

  const handlePdfExport = () => {
    const rows = exportRows();
    printInventorySnapshot({
      restaurant_name: "Kitchen",
      generated_at: new Date().toISOString(),
      currency: getCurrency(),
      items: rows.map((r) => ({
        name: r.name,
        category: r.category,
        quantity: r.quantity,
        par_level: r.par_level,
        unit: r.unit,
        unit_price: r.unit_price,
        stock_value: r.stock_value,
        below_par: r.below_par,
      })),
      low_stock: rows.filter((r) => r.below_par),
    });
  };

  const invTourSteps = useMemo(() => ([
    {
      selector: '[data-testid="inventory-import-btn"]',
      title: "Import your whole stock",
      body: "Upload a CSV or Excel and we will map units, quantities and par levels into the app in one step.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="inventory-share-btn"]',
      title: "Share with a supplier",
      body: "Generate a public read-only link with just the low-stock items. Perfect to WhatsApp your produce guy without exposing your recipe book.",
      placement: "bottom",
    },
    {
      selector: '[data-testid="inventory-log-batch-btn"]',
      title: "Log a prep batch",
      body: "When you butcher or reduce a stock, log the yield here. Every recipe that uses the item re-costs to match the new true yield.",
      placement: "bottom",
    },
  ]), []);
  const invTour = useModuleTour("inventory_v1", invTourSteps);

  return (
    <div className="p-4 sm:p-6 lg:p-10 max-w-6xl mx-auto space-y-6" data-testid="inventory-page">
      {invTour.TourNode}
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-eyebrow flex items-center gap-2"><Package className="w-3 h-3" /> Stock</div>
          <h1 className="font-display text-3xl md:text-4xl leading-none mt-2">Inventory</h1>
          <p className="text-muted-foreground mt-2 text-sm max-w-md">Track on-hand quantities and min stock levels. Get flagged with a red LOW alert when you drop below.</p>
        </div>
        <div className="flex items-center gap-2">
          <TourButton onClick={invTour.start} testId="inventory-tour-btn" />
          <Button
            variant="outline"
            onClick={() => setImportOpen(true)}
            className="gap-2"
            data-testid="inventory-import-btn"
          >
            <Upload className="w-4 h-4" /> Import CSV / Excel
          </Button>
          <Button
            variant="outline"
            onClick={() => setNewIngredientOpen(true)}
            className="gap-2"
            data-testid="inventory-new-ingredient"
          >
            <Plus className="w-4 h-4" /> Add new ingredient
          </Button>
          <Button
            variant="outline"
            onClick={() => setShareOpen(true)}
            className="gap-2"
            data-testid="inventory-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share / Order
          </Button>
          <Button
            variant="outline"
            onClick={() => setBatchDialogOpen(true)}
            className="gap-2"
            data-testid="inventory-log-batch-btn"
          >
            <Scale className="w-4 h-4" /> Log prep batch
          </Button>
          <DataExportButton
            onCsv={handleCsvExport}
            onXlsx={handleXlsxExport}
            onPdf={handlePdfExport}
            label="Export"
            testId="inventory-export"
          />
          <Button onClick={saveAll} disabled={saving || !Object.keys(edits).length} className="gap-2" data-testid="inventory-save-all">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes ({Object.keys(edits).length})
          </Button>
        </div>
      </header>

      {lowStock.length > 0 && (
        <div className="rounded-2xl border border-destructive/40 bg-destructive/10 p-5" data-testid="low-stock-alert">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <div className="label-eyebrow text-destructive">Low stock • {lowStock.length}</div>
          </div>
          <div className="text-sm text-foreground/85 space-y-1">
            {lowStock.map(({ ing, inv }) => (
              <div key={ing.id} data-testid={`low-${ing.id}`}>
                <span className="font-medium">{ing.name}</span>: {inv.quantity} / {inv.par_level} {ing.purchase_unit}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="inventory-skeleton">
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} lines={3} />)}
        </div>
      ) : ingredients.length === 0 ? (
        <EmptyState
          illustration="empty_plate"
          eyebrow="Get started"
          title="Your pantry is empty."
          body="Add your first ingredient to start tracking on hand quantities, minimum stock, and true food cost per plate."
          ctaLabel="Add first ingredient"
          ctaIcon={Plus}
          onCtaClick={() => setNewIngredientOpen(true)}
          ctaTestId="empty-state-new-ingredient"
          secondaryLabel="Manage the library"
          onSecondaryClick={() => (window.location.href = "/ingredients")}
        />
      ) : (
        <div className="rounded-2xl glass-card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left border-b border-border">
                <th className="px-4 py-3 label-eyebrow font-bold">Ingredient</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Unit</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">On hand</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Min stock level</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right">Est. value</th>
                <th className="px-4 py-3 label-eyebrow font-bold text-right w-12"></th>
              </tr>
            </thead>
            <tbody>
              {ingredients.map((ing) => {
                const row = getRow(ing);
                const perUnit = ing.purchase_qty * (ing.yield_percent / 100);
                const value = perUnit > 0 ? (row.quantity / perUnit) * ing.purchase_price : 0;
                const isEdited = !!edits[ing.id];
                const below = row.par_level > 0 && row.quantity < row.par_level;
                return (
                  <tr
                    key={ing.id}
                    className={`border-b border-border/60 ${below ? "bg-destructive/10 border-l-2 border-l-destructive" : isEdited ? "bg-secondary/40" : ""}`}
                    data-testid={`inv-row-${ing.id}`}
                  >
                    <td className="px-4 py-2 font-medium">
                      <div className="flex items-center gap-2">
                        {ing.name}
                        {below && <span className="text-[0.55rem] px-1.5 py-0.5 rounded-full bg-destructive text-destructive-foreground font-bold tracking-wider" data-testid={`low-badge-${ing.id}`}>LOW</span>}
                      </div>
                      <div className="text-xs text-muted-foreground capitalize">{ing.category}</div>
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{ing.purchase_unit}</td>
                    <td className="px-4 py-2 text-right">
                      <Input
                        type="number" step="0.01"
                        value={row.quantity}
                        onChange={(e) => setRow(ing.id, { quantity: e.target.value })}
                        className="w-24 text-right tabular ml-auto border-white/10 hover:border-primary/40 focus:border-primary/60 transition-colors"
                        title="Click to edit on-hand quantity"
                        data-testid={`inv-qty-${ing.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Input
                        type="number" step="0.01"
                        value={row.par_level}
                        onChange={(e) => setRow(ing.id, { par_level: e.target.value })}
                        className="w-24 text-right tabular ml-auto border-white/10 hover:border-primary/40 focus:border-primary/60 transition-colors"
                        title="Click to edit minimum stock level"
                        data-testid={`inv-par-${ing.id}`}
                      />
                    </td>
                    <td className="px-4 py-2 text-right tabular">{formatMoney(value)}</td>
                    <td className="px-2 py-2 text-right">
                      <button
                        onClick={() => removeRow(ing)}
                        className="w-8 h-8 rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center transition-colors"
                        aria-label={`Remove ${ing.name} from stock`}
                        data-testid={`inv-remove-${ing.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <InventoryShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        lowStockCount={lowStock.length}
        itemCount={ingredients.length}
      />
      <InventoryImportDialog
        open={importOpen}
        onOpenChange={setImportOpen}
        onDone={load}
      />
      <IngredientForm
        open={newIngredientOpen}
        onOpenChange={setNewIngredientOpen}
        initial={null}
        onSaved={(saved) => { toast.success(`Added: ${saved.name}`); load(); }}
      />
      <LogPrepBatchDialog
        open={batchDialogOpen}
        onOpenChange={setBatchDialogOpen}
        ingredients={ingredients}
        onLogged={() => { load(); }}
      />
    </div>
  );
}
